import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('ENTITY_GET');

export const entityGetCommand: SelfContainedCommand = {
  id: 'entity_get',
  description: `Get an entity by its ID. Returns the complete entity with all metadata and content.

**SUPPORTED ENTITIES:**
- All entity types (Documents, Notes, Sheets, Canvas, Presentations, Folders, Files)

**Use Cases:**
- Retrieve entity details
- Check entity existence
- Get entity metadata
- Access entity skeleton data
- **Get APIConnection credentials** - Read APIConnection entities to get API keys, tokens, and configuration for external APIs
  - Use this BEFORE starting compute sessions that need API access
  - The skeleton contains: baseUrl, authType, credentials (encrypted), defaultHeaders, and serviceName

**For API Workflows:**
1. Use space_get_context to find APIConnection entities in the workspace
2. Use entity_get to read the specific APIConnection
3. Extract credentials from the skeleton
4. Pass credentials to compute_session via env_vars parameter`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'The ID of the entity to retrieve',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { entityId: 'doc_123' },
        expectedOutput: createSuccessResult('Entity retrieved', {
          entity: { _id: 'doc_123', name: 'Document', entityType: 'Document' }
        }),
        description: 'Get entity by ID',
        context: 'When checking entity details'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 200,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { entityId } = params;
    
    try {
      // ✅ Resolve llmId to _id if needed
      const resolution = resolveEntityId(entityId);
      
      if (resolution.isLlmId && !resolution.entity) {
        return createErrorResult(
          'Entity not found',
          `No entity found with llmId: ${entityId}`,
          'validation',
          { data: { providedId: entityId } }
        );
      }
      
      entityId = resolution.resolved;
      
      log.info('[entity_get] Getting entity:', { 
        originalId: params.entityId,
        resolvedId: entityId,
        wasLlmId: resolution.isLlmId
      });
      
      const entityStore = useEntityStore.getState();
      const entity = await entityStore.actions.getEntity(entityId);
      
      log.info('[entity_get] ✅ Entity retrieved:', { id: entity._id });
      
      return createSuccessResult(
        'Entity retrieved successfully',
        { entity },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions'
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[entity_get] Error:', error);
      return createErrorResult(
        'Failed to retrieve entity',
        `Failed to get entity: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { entityId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

